import api from './api';
import { ContentBlock, DynamicPage } from './dynamicPageService';

/**
 * Admin-Service: CMS-Seiten (Impressum, Datenschutz, …) – Flutter-Parität.
 *
 * Endpunkte: /api/admin/pages (requiresAuth + onlyAdmin)
 *   GET    /api/admin/pages                     → { data: pages[] } (auch unsichtbare)
 *   GET    /api/admin/pages/:id                 → { data: page }
 *   POST   /api/admin/pages                     → 201 { data: page }
 *   PUT    /api/admin/pages/:id                 → { data: page }
 *   DELETE /api/admin/pages/:id
 *   POST   /api/admin/pages/:id/toggle-visibility
 */

const parseBlock = (b: any): ContentBlock => ({
  id: b?.blockId ?? b?._id ?? '',
  type: b?.type ?? 'paragraph',
  text: b?.text ?? '',
  level: b?.level ?? 2,
  imageUrl: b?.imageUrl ?? '',
  imageAlt: b?.imageAlt ?? '',
  imageCaption: b?.imageCaption ?? '',
  imageMaxWidth: typeof b?.imageMaxWidth === 'number' ? b.imageMaxWidth : null,
  listItems: Array.isArray(b?.listItems) ? b.listItems.filter((x: any) => typeof x === 'string') : [],
  listType: b?.listType ?? 'bullet',
  order: b?.order ?? 0,
});

const parsePage = (d: any): DynamicPage | null => {
  if (!d || typeof d !== 'object') return null;
  return {
    id: d._id ?? d.id ?? '',
    slug: d.slug ?? '',
    title: d.title ?? '',
    metaDescription: d.metaDescription ?? '',
    contentBlocks: Array.isArray(d.contentBlocks) ? d.contentBlocks.map(parseBlock) : [],
    isVisible: d.isVisible ?? true,
    showLastUpdated: d.showLastUpdated ?? true,
    createdAt: d.createdAt ?? null,
    updatedAt: d.updatedAt ?? null,
  };
};

/** Block fürs Backend serialisieren (blockId + order aus Index). */
const serializeBlock = (block: ContentBlock, index: number) => ({
  blockId: block.id || (typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `block-${Date.now()}-${index}`),
  type: block.type,
  text: block.text ?? '',
  level: block.level ?? 2,
  imageUrl: block.imageUrl ?? '',
  imageAlt: block.imageAlt ?? '',
  imageCaption: block.imageCaption ?? '',
  imageMaxWidth: typeof block.imageMaxWidth === 'number' ? block.imageMaxWidth : null,
  listItems: block.listItems ?? [],
  listType: block.listType ?? 'bullet',
  order: index,
});

class AdminPagesService {
  async listPages(): Promise<DynamicPage[]> {
    const res = await api.get('/api/admin/pages');
    const list = res.data?.data ?? [];
    return (Array.isArray(list) ? list : []).map(parsePage).filter(Boolean) as DynamicPage[];
  }

  async getPage(id: string): Promise<DynamicPage | null> {
    const res = await api.get(`/api/admin/pages/${id}`);
    return parsePage(res.data?.data);
  }

  async createPage(payload: { slug: string; title: string; metaDescription?: string }): Promise<DynamicPage | null> {
    const res = await api.post('/api/admin/pages', {
      slug: payload.slug.trim().toLowerCase(),
      title: payload.title.trim(),
      metaDescription: payload.metaDescription ?? '',
      contentBlocks: [],
      isVisible: false, // neu angelegte Seiten starten unsichtbar
      showLastUpdated: true,
    });
    return parsePage(res.data?.data);
  }

  async updatePage(
    id: string,
    page: Pick<DynamicPage, 'slug' | 'title' | 'metaDescription' | 'isVisible' | 'showLastUpdated'>,
    blocks: ContentBlock[]
  ): Promise<DynamicPage | null> {
    const res = await api.put(`/api/admin/pages/${id}`, {
      slug: page.slug.trim().toLowerCase(),
      title: page.title.trim(),
      metaDescription: page.metaDescription ?? '',
      isVisible: page.isVisible,
      showLastUpdated: page.showLastUpdated,
      contentBlocks: blocks.map(serializeBlock),
    });
    return parsePage(res.data?.data);
  }

  async deletePage(id: string): Promise<void> {
    await api.delete(`/api/admin/pages/${id}`);
  }

  async toggleVisibility(id: string): Promise<DynamicPage | null> {
    const res = await api.post(`/api/admin/pages/${id}/toggle-visibility`);
    return parsePage(res.data?.data);
  }

  /** Bild hochladen (POST /api/file, ROHES Base64) → /api/file/-URL. */
  async uploadImage(file: File): Promise<string> {
    const dataUrl: string = await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(String(reader.result));
      reader.onerror = () => reject(new Error('Datei konnte nicht gelesen werden'));
      reader.readAsDataURL(file);
    });
    const base64 = dataUrl.includes('base64,') ? dataUrl.split('base64,')[1] : dataUrl;
    const res = await api.post('/api/file', { data: base64 });
    const name = res.data?.name;
    if (!name) throw new Error('Upload fehlgeschlagen');
    return `/api/file/${name}`;
  }

  buildFileUrl(path?: string | null): string | null {
    if (!path) return null;
    if (path.startsWith('http')) return path;
    return `${api.defaults.baseURL}${path.startsWith('/') ? '' : '/'}${path}`;
  }

  getErrorMessage(err: unknown, fallback: string): string {
    const anyErr = err as { response?: { data?: { error?: string } | string } };
    const data = anyErr?.response?.data;
    if (typeof data === 'string' && data.trim()) return data;
    if (data && typeof data === 'object' && typeof data.error === 'string') return data.error;
    return fallback;
  }

  /** Slug-Vorschlag aus dem Titel. */
  slugify(title: string): string {
    return title
      .toLowerCase()
      .replace(/ä/g, 'ae').replace(/ö/g, 'oe').replace(/ü/g, 'ue').replace(/ß/g, 'ss')
      .normalize('NFD').replace(/[̀-ͯ]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .substring(0, 80);
  }
}

const adminPagesService = new AdminPagesService();
export default adminPagesService;
